import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import Skeleton from 'react-loading-skeleton';
import { formatUsPhone } from '../../../utilities/formatPhone';
import '../../../styles/viewLinkedPolicy.css';

export type PolicyDetail = {
  databaseId?: string;
  number?: string;
  policyNumber?: string;       // fallback alias
  isQuote?: boolean;
  effectiveDate?: string | null;
  expirationDate?: string | null;
  inceptionDate?: string | null;
  bindDate?: string | null;
  businessType?: string | null;
  businessSubType?: string | null;
  description?: string | null;
  billingType?: string | null;
  lineOfBusinesses?: { lineOfBusinessName?: string }[];
  linesOfBusiness?: string[];  // fallback alias
  carrierName?: string | null;
  carrierNAIC?: string | null;
  mgaName?: string | null;
  mgaFinanceSearchCoCode?: string | null;
  totalPremium?: number;
  totalNonPremium?: number;
  totalAgencyCommission?: number;
  premiumSent?: string | null;
  changeDate?: string | null;
  createDate?: string | null;
  cancellationDate?: string | null;
  reinstatementDate?: string | null;
  active?: boolean;
  status?: string | null;
  additionalPolicyStatus?: string | null;
  policyTerm?: number;
  policyTermFormatted?: string | null;
  policyTermInMonths?: number;  // fallback alias
  billingCompany?: { name?: string } | null;
  billingCompanyName?: string;
  renewedPolicyDetails?: { number?: string } | null;
  renewedPolicyNumber?: string | null;
  insuredDatabaseId?: string;
  insuredCommercialName?: string | null;
  insuredFirstName?: string | null;
  insuredLastName?: string | null;
  insuredEmail?: string | null;
  insuredPhoneNumber?: string | null;
  insuredAddressLine1?: string | null;
  insuredAddressLine2?: string | null;
  insuredCity?: string | null;
  insuredState?: string | null;
  insuredZipCode?: string | null;
  insuredMC_Number?: string | null;
  insuredDOT_Number?: string | null;
  origin?: string | null;
  lastChangeUserName?: string | null;
  customFields?: string | null;
};

export type ViewLinkedPolicyProps = {
  visible: boolean;
  onHide: () => void;
  policy: PolicyDetail | null;
  loading?: boolean;
};

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = iso ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(value?: number): string {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function getStatusSeverity(status?: string | null): 'success' | 'warning' | 'danger' | 'info' {
  if (!status) return 'info';
  if (status === 'Active') return 'success';
  if (status === 'Expired' || status === 'Cancelled' || status === 'Flat Cancel') return 'danger';
  return 'warning';
}

function getPolicyTypeFromCustomFields(customFields?: string | null): string | null {
  if (!customFields) return null;
  try {
    const parsed = JSON.parse(customFields);
    if (!Array.isArray(parsed)) return null;
    const entry = parsed.find((item: any) => item?.Key === 'PolicyType');
    return entry?.Value || null;
  } catch {
    return null;
  }
}

const ViewLinkedPolicy: React.FC<ViewLinkedPolicyProps> = ({ visible, onHide, policy, loading }) => {
  const policyNumber = policy?.number || policy?.policyNumber || '-';

  const normalizeLob = (s: string) => s.replace(/\bWorker's Compensation\b/g, "Workers' Compensation");

  const lob = normalizeLob(
    Array.isArray(policy?.lineOfBusinesses) && policy!.lineOfBusinesses!.length > 0
      ? policy!.lineOfBusinesses!.map(l => l.lineOfBusinessName || '').filter(Boolean).join(', ')
      : Array.isArray(policy?.linesOfBusiness) && policy!.linesOfBusiness!.length > 0
        ? policy!.linesOfBusiness!.join(', ')
        : '-'
  );

  const termLabel = policy?.policyTermFormatted || (
    (policy?.policyTerm ?? policy?.policyTermInMonths)
      ? `${policy?.policyTerm ?? policy?.policyTermInMonths} month${(policy?.policyTerm ?? policy?.policyTermInMonths) !== 1 ? 's' : ''}`
      : '-'
  );

  const policyType = getPolicyTypeFromCustomFields(policy?.customFields) ?? lob;

  const insuredName = policy?.insuredCommercialName
    || [policy?.insuredFirstName, policy?.insuredLastName].filter(Boolean).join(' ')
    || '-';

  const insuredAddress = [
    policy?.insuredAddressLine1,
    policy?.insuredAddressLine2,
    policy?.insuredCity,
    policy?.insuredState,
    policy?.insuredZipCode,
  ].filter(Boolean).join(', ') || '-';

  const header = (
    <div className="vlp__dialogHeader">
      <i className="pi pi-shield vlp__dialogHeaderIcon" />
      <div>
        <div className="vlp__dialogTitle">Policy Details</div>
        <div className="vlp__dialogSubtitle">{loading ? 'Loading…' : policyNumber}</div>
      </div>
    </div>
  );

  return (
    <Dialog
      header={header}
      visible={visible}
      onHide={onHide}
      className="vlp__dialog"
      modal
      draggable={false}
      resizable={false}
    >
      {loading ? (
        <div className="vlp__body">
          {/* Status badges */}
          <div className="vlp__statusRow">
            <Skeleton width={62} height={24} borderRadius={12} />
            <Skeleton width={58} height={24} borderRadius={12} />
          </div>

          {/* Policy Information */}
          <div className="vlp__section">
            <div className="vlp__sectionLabel">
              <Skeleton width={130} height={10} borderRadius={3} />
            </div>
            <div className="vlp__grid">
              {[120, 140, 90].map((w, i) => (
                <div key={i} className="vlp__field">
                  <Skeleton width={80} height={9} borderRadius={3} />
                  <Skeleton width={w} height={14} borderRadius={3} />
                </div>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="vlp__section">
            <div className="vlp__sectionLabel">
              <Skeleton width={60} height={10} borderRadius={3} />
            </div>
            <div className="vlp__grid vlp__grid--3col">
              {[90, 90, 90].map((w, i) => (
                <div key={i} className="vlp__field">
                  <Skeleton width={90} height={9} borderRadius={3} />
                  <Skeleton width={w} height={14} borderRadius={3} />
                </div>
              ))}
            </div>
          </div>

          {/* Insured */}
          <div className="vlp__section">
            <div className="vlp__sectionLabel">
              <Skeleton width={70} height={10} borderRadius={3} />
            </div>
            <div className="vlp__grid">
              {[140, 170, 110, 200].map((w, i) => (
                <div key={i} className="vlp__field">
                  <Skeleton width={60} height={9} borderRadius={3} />
                  <Skeleton width={w} height={14} borderRadius={3} />
                </div>
              ))}
            </div>
          </div>

          {/* Timestamps */}
          <div className="vlp__timestamps">
            <Skeleton width={130} height={10} borderRadius={3} />
            <Skeleton width={220} height={10} borderRadius={3} />
          </div>
        </div>
      ) : !policy ? null : (
        <div className="vlp__body">

          {/* Status row */}
          <div className="vlp__statusRow">
            <Tag value={policy.status || 'Unknown'} severity={getStatusSeverity(policy.status)} />
            {policy.isQuote
              ? <Tag value="Quote" severity="info" />
              : <Tag value="Policy" className="vlp__tagPolicy" />}
            {/* {policy.businessType && (
              <Tag value={policy.businessType} style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }} />
            )} */}
          </div>

          {/* Policy Information */}
          <div className="vlp__section">
            <div className="vlp__sectionLabel"><i className="pi pi-file" />Policy Information</div>
            <div className="vlp__grid">
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Policy Number</div>
                <div className="vlp__fieldValue vlp__fieldValue--mono">{policyNumber}</div>
              </div>
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Line of Business</div>
                <div
                  className="vlp__fieldValue"
                  title={policyType !== lob && lob !== '-' ? lob : undefined}
                  style={policyType !== lob && lob !== '-' ? { textDecoration: 'underline dotted' } : undefined}
                >
                  {policyType}
                </div>
              </div>
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Policy Term</div>
                <div className="vlp__fieldValue">{termLabel}</div>
              </div>
              {/* <div className="vlp__field">
                <div className="vlp__fieldLabel">Business Type</div>
                <div className="vlp__fieldValue">{policy.businessType || '-'}</div>
              </div> */}
              {/* {policy.origin && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Origin</div>
                  <div className="vlp__fieldValue">{policy.origin}</div>
                </div>
              )} */}
              {policy.description && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Description</div>
                  <div className="vlp__fieldValue">{policy.description}</div>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="vlp__section">
            <div className="vlp__sectionLabel"><i className="pi pi-calendar" />Dates</div>
            <div className="vlp__grid vlp__grid--3col">
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Effective Date</div>
                <div className="vlp__fieldValue">{formatDate(policy.effectiveDate)}</div>
              </div>
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Expiration Date</div>
                <div className="vlp__fieldValue">{formatDate(policy.expirationDate)}</div>
              </div>
              {/* <div className="vlp__field">
                <div className="vlp__fieldLabel">Inception Date</div>
                <div className="vlp__fieldValue">{formatDate(policy.inceptionDate || policy.effectiveDate)}</div>
              </div> */}
              {policy.bindDate && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Bind Date</div>
                  <div className="vlp__fieldValue">{formatDate(policy.bindDate)}</div>
                </div>
              )}
              {policy.cancellationDate && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Cancellation Date</div>
                  <div className="vlp__fieldValue vlp__fieldValue--danger">{formatDate(policy.cancellationDate)}</div>
                </div>
              )}
              {policy.reinstatementDate && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Reinstatement Date</div>
                  <div className="vlp__fieldValue">{formatDate(policy.reinstatementDate)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Insured */}
          <div className="vlp__section">
            <div className="vlp__sectionLabel"><i className="pi pi-user" />Insured</div>
            <div className="vlp__grid">
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Name</div>
                <div className="vlp__fieldValue">{insuredName}</div>
              </div>
              {policy.insuredEmail && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Email</div>
                  <div className="vlp__fieldValue">{policy.insuredEmail}</div>
                </div>
              )}
              {policy.insuredPhoneNumber && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Phone</div>
                  <div className="vlp__fieldValue">{formatUsPhone(policy.insuredPhoneNumber)}</div>
                </div>
              )}
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Address</div>
                <div className="vlp__fieldValue">{insuredAddress}</div>
              </div>
              {/* {policy.insuredMC_Number && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">MC Number</div>
                  <div className="vlp__fieldValue vlp__fieldValue--mono">{policy.insuredMC_Number}</div>
                </div>
              )} */}
              {policy.insuredDOT_Number && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">DOT Number</div>
                  <div className="vlp__fieldValue vlp__fieldValue--mono">{policy.insuredDOT_Number}</div>
                </div>
              )}
            </div>
          </div>

          {/* Carrier & Billing */}
          {/* <div className="vlp__section">
            <div className="vlp__sectionLabel"><i className="pi pi-building" />Carrier & Billing</div>
            <div className="vlp__grid">
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Carrier</div>
                <div className="vlp__fieldValue">{policy.carrierName || '-'}</div>
              </div>
              {policy.carrierNAIC && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Carrier NAIC</div>
                  <div className="vlp__fieldValue vlp__fieldValue--mono">{policy.carrierNAIC}</div>
                </div>
              )}
              <div className="vlp__field">
                <div className="vlp__fieldLabel">MGA</div>
                <div className="vlp__fieldValue">{policy.mgaName || '-'}</div>
              </div>
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Billing Company</div>
                <div className="vlp__fieldValue">
                  {policy.billingCompany?.name || policy.billingCompanyName || '-'}
                </div>
              </div>
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Billing Type</div>
                <div className="vlp__fieldValue">{policy.billingType || '-'}</div>
              </div>
              {policy.premiumSent && (
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Premium Sent</div>
                  <div className="vlp__fieldValue">{policy.premiumSent}</div>
                </div>
              )}
            </div>
          </div> */}

          {/* Financials */}
          {/* <div className="vlp__section vlp__section--last">
            <div className="vlp__sectionLabel"><i className="pi pi-dollar" />Financials</div>
            <div className="vlp__grid vlp__grid--3col">
              <div className="vlp__field vlp__field--highlight">
                <div className="vlp__fieldLabel">Total Premium</div>
                <div className="vlp__fieldValue vlp__fieldValue--bold">{formatCurrency(policy.totalPremium)}</div>
              </div>
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Non-Premium</div>
                <div className="vlp__fieldValue">{formatCurrency(policy.totalNonPremium)}</div>
              </div>
              <div className="vlp__field">
                <div className="vlp__fieldLabel">Agency Commission</div>
                <div className="vlp__fieldValue">{formatCurrency(policy.totalAgencyCommission)}</div>
              </div>
            </div>
          </div> */}

          {/* Renewal */}
          {(policy.renewedPolicyDetails?.number || policy.renewedPolicyNumber) && (
            <div className="vlp__section vlp__section--last">
              <div className="vlp__sectionLabel"><i className="pi pi-refresh" />Renewal</div>
              <div className="vlp__grid">
                <div className="vlp__field">
                  <div className="vlp__fieldLabel">Renewed Policy</div>
                  <div className="vlp__fieldValue vlp__fieldValue--mono">
                    {policy.renewedPolicyDetails?.number || policy.renewedPolicyNumber}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="vlp__timestamps">
            <span>Created: {formatDate(policy.createDate)}</span>
            <span>Modified by: {policy.lastChangeUserName || '-'} · {formatDate(policy.changeDate)}</span>
          </div>

        </div>
      )}
    </Dialog>
  );
};

export default ViewLinkedPolicy;
